/* Core & libraries */
import React, { useState, useEffect } from 'react';
import { useTheme } from "react-jss";

/* Helpers, constants, custom methods, internal libraries */
import { Field, Formik } from "formik";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useCompaniesService } from "../../../services/companiesService";
import { useCompanyDeleteDetailsService } from '../../../services/companyDeleteDetailsService';
import { useUserContext } from "../../../contexts/userContext";
import { useNavigate } from "react-router";
import { useLocaleContext } from "../../../contexts/localeContext";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { AIRLINES } from "../../../constants/appConstants";

/* Components */
import UCInput from "../../ucinput";
import UCModal from "../../ucmodal";
import DeleteFooter from "./footers/deleteFooter";
import CloseFooter from "./footers/closeFooter";
import ConfirmDeleteFooter from "./footers/confirmDeleteFooter";
import ModalContentSkeleton from './skeleton/deleteCompanyMasterModalSkeletonStructure';

/* Styles */
import styles from "./styles";

const messages = defineMessages({
  removePlaceholder: {
    id: 'settings.removePlaceholder'
  },
  fraudCategoryPlaceholder: {
    id: 'settings.confirmDeleteAction.aaAdmin.fraudCategoryPlaceholder'
  },
  days: {
    id: 'settings.confirmDeleteAction.removalWillBeScheduled.description4.days'
  }
})

const DeleteCompanyMasterModal = ({ showModal, setShowModal, modalIcon, aaAdminCompanyEntityId, aaAdminCompanyName, setSelectedRow }) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const userDetails = useUserContext();
  const intl = useIntl();
  const navigate = useNavigate();
  const globalLocale = useLocaleContext();
  const { checkPermission } = useHasPermission();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const companyName = userDetails?.userInfo?.companyInfo?.companyName
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const isAA = providerCode === AIRLINES.AA;

  const { deleteCompany } = useCompaniesService();
  const { getCompanyRemovalDetails, postCompanyRemoval, removalDetails, removalStatus, resetRemovalDetails } = useCompanyDeleteDetailsService(aaAdminCompanyEntityId, providerCode);

  const [hasFetchedDetails, setHasFetchedDetails] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', description: '', footer: null });
  const [removeInput, setRemoveInput] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [removalDueToFraud, setRemovalDueToFraud] = useState(false)
  const [fraudCategory, setFraudCategory] = useState("")
  const [removalImmediately, setRemovalImmediately] = useState(false)

  const fraudRegex = /^[a-zA-ZáàâãäåæçéèêëíìîïñóòôõöúùûüÁÀÂÃÄÅÆÇÉÈÊËÍÌÎÏÑÓÒÔÕÖÚÙÛÜ' -]*$/;
  const [fraudError, setFraudError] = useState(false);

  useEffect(() => {
    const handleGetCompanyRemovalDetails = async () => {
      if (!showModal || hasFetchedDetails) return;

      if (isAA && (aaAdminCompanyEntityId || companyEntityId)) {
        await getCompanyRemovalDetails();
        setHasFetchedDetails(true);
      } else {
        // Non-AA: skip fetch, show confirm immediately
        setHasFetchedDetails(true);
        setConfirmDelete(true);
      }
    };

    handleGetCompanyRemovalDetails();
  }, [showModal, aaAdminCompanyEntityId, companyEntityId, getCompanyRemovalDetails, hasFetchedDetails, isAA]);

  useEffect(() => {
    if (removalDetails || confirmDelete) {
      setModalContent(getModalContent());
    }
  }, [removalDetails, confirmDelete, removeInput, removalDueToFraud, fraudCategory, removalImmediately, globalLocale]);

  let content = {
    title: '',
    description: '',
    footer: null,
  };

  const goBack = () => {
    navigate(-1);
  }

  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setShowModal(false);
    setRemoveInput("")
    setRemovalDueToFraud(false)
    setFraudCategory("")
    setFraudError(false);
  };

  const handleConfirmDelete = async () => {
    await postCompanyRemoval(removalDueToFraud, fraudCategory, removalImmediately);
    handleCancelDelete()
    setShowModal(false);
  };

  const deleteAction = async () => {
    if (removeInput === intl.formatMessage(messages.removePlaceholder)) {
      setDeleteErrorMessage(false);

      const response = await deleteCompany({
        companyId: aaAdminCompanyEntityId ? aaAdminCompanyEntityId : companyEntityId,
        companyName: aaAdminCompanyName ? aaAdminCompanyName : companyName
      })
      if (response) {
        setShowModal(false)
        goBack();
      }
    } else {
      // todo
      setDeleteErrorMessage(true);
      console.log("error")
    }
  }

  const getDeleteContent = () => (
    <>
      <FormattedMessage id="settings.deleteAction.description" />
      <p className={classes.deleteActionNote}><FormattedMessage id="settings.deleteAction.note" /></p>
      <UCInput
        inputClassName={classes.removeInput}
        placeholder={intl.formatMessage(messages.removePlaceholder)}
        onChange={event => { setRemoveInput(event.target.value) }}
      />
      {
        deleteErrorMessage &&
        <div className={classes.error}><FormattedMessage id={"settings.deleteAction.error"} /></div>
      }
    </>
  );
  const handleChange = (e) => {
    const val = e.target.value;
    setFraudCategory(val);

    if (!fraudRegex.test(val)) {
      setFraudError(true);
    } else {
      setFraudError(false);
    }
  };

  const getConfirmDeleteContent = (removalWillBeScheduled, openTripCreditsExists) => (
    <>
      <FormattedMessage id="settings.confirmDeleteAction.description" />
      {removalWillBeScheduled === false &&
        <div className={classes.confirmDeleteModalAdditionalText}>
          <span className={classes.noteText}><FormattedMessage id="settings.confirmDeleteAction.futurePNRs.description1" /></span>
          <FormattedMessage id="settings.confirmDeleteAction.futurePNRs.description2" />
        </div>
      }
      {
        deleteErrorMessage &&
        <div className={classes.error}><FormattedMessage id={"settings.deleteAction.error"} /></div>
      }

      {removalWillBeScheduled && (
        <div className={classes.confirmDeleteModalAdditionalText}>
          <p className={classes.removalWillBeScheduled}>
            <FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description3" />
          </p>
          <p className={classes.removalWillBeScheduled}>
            <FormattedMessage
              id="settings.confirmDeleteAction.removalWillBeScheduled.description4"
              values={{
                numberOfDays: (
                  <span className={classes.numberOfDays}>
                    {`${removalDetails?.daysToRemove} ${intl.formatMessage((messages.days))}`}
                  </span>
                ),
              }}
            />
          </p>
          <p className={classes.removalWillBeScheduled}>
            <FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description5" />
          </p>
        </div>
      )}

      {openTripCreditsExists === true && (
        <div className={classes.confirmDeleteModalAdditionalText}>
          {/* <p className={classes.removalWillBeScheduled}><span className={classes.noteText}><FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description1" /></span>
          <FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description2" /></p>
          <p className={classes.removalWillBeScheduled}><FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description3" /></p>
          <p className={classes.removalWillBeScheduled}><FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description4" values={{ numberOfDays: `${removalDetails?.daysToRemove}` }} /></p>
          <FormattedMessage id="settings.confirmDeleteAction.removalWillBeScheduled.description5" /> */}
        </div>
      )}

      {isAA && checkPermission("F_COMPANY_REMOVAL_SETREASON") && (
        <Formik
        // innerRef={formRef}
        // enableReinitialize={true}
        // onSubmit={(values, { setSubmitting }) => {
        //   submitForm(values, setSubmitting);
        // }}
        >
          {({
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className={classes.removeDueToFraudText}>
                <FormattedMessage id="settings.confirmDeleteAction.aaAdmin.selectAReason" />
              </div>
              <div className="form-check">
                <input
                  onChange={() => {
                    setRemovalDueToFraud(false);
                    setFraudError(false)
                  }}
                  className={`form-check-input shadow-none ${classes.checkbox}`}
                  type="radio"
                  name="removalDueToFraud"
                  id="removalNo"
                  value="no"
                  checked={removalDueToFraud === false}
                />
                <label className="form-check-label" htmlFor="removalNo">
                  <FormattedMessage id="settings.confirmDeleteAction.aaAdmin.radio1" />
                </label>
              </div>
              <div className="form-check">
                <input
                  onChange={() => setRemovalDueToFraud(true)}
                  className={`form-check-input shadow-none ${classes.checkbox}`}
                  type="radio"
                  name="removalDueToFraud"
                  id="removalYes"
                  value="yes"
                  checked={removalDueToFraud === true}
                />
                <label className="form-check-label" htmlFor="removalYes">
                  <FormattedMessage id="settings.confirmDeleteAction.aaAdmin.radio2" />
                </label>
              </div>

              {removalDetails?.allowOverride && removalDetails?.removalWillBeScheduled && removalDueToFraud === false && (
                <div>
                  <div className={classes.removeDueToFraudText}>
                    <FormattedMessage id="settings.confirmDeleteAction.aaAdmin.removalTime" />
                  </div>
                  <div className="form-check">
                    <input
                      onChange={() => setRemovalImmediately(false)}
                      className={`form-check-input shadow-none ${classes.checkbox}`}
                      type="radio"
                      name="removalTime"
                      id="removalNumberOfDays"
                      value="removalNumberOfDays"
                      checked={removalImmediately === false}
                    />
                    <label className="form-check-label" htmlFor="removalNumberOfDays">
                      <FormattedMessage id="settings.confirmDeleteAction.aaAdmin.removalTimeRadio1" values={{ numberOfDays: `${removalDetails?.daysToRemove}` }} />
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      onChange={() => setRemovalImmediately(true)}
                      className={`form-check-input shadow-none ${classes.checkbox}`}
                      type="radio"
                      name="removalTime"
                      id="removalImmediately"
                      value="Immediately"
                      checked={removalImmediately === true}
                    />
                    <label className="form-check-label" htmlFor="removalImmediately">
                      <FormattedMessage id="settings.confirmDeleteAction.aaAdmin.removalTimeRadio2" />
                    </label>
                  </div>
                </div>
              )}

              {removalDueToFraud && (
                <div className={fraudError && classes.inputContainer}>
                  <Field
                    type="select"
                    name="fraudCategory"
                    id="fraudCategory"
                    render={({ form }) => (
                      <div>
                        <UCInput
                          inputClassName={fraudError ? classes.inputContainer : classes.fraudCategoryInput}
                          placeholder={intl.formatMessage(messages.fraudCategoryPlaceholder)}
                          //onChange={event => setFraudCategory(event.target.value)}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  />
                  {fraudError && <div className={classes.nameError}>
                    <FormattedMessage id="delete.company.fraud.error" />
                  </div>}
                </div>
              )}
            </form>
          )}
        </Formik>
      )}
    </>
  )

  const renderNonAAContent = () => {



    content.title = (
      <FormattedMessage
        id="settings.confirmDeleteAction.title"
        values={{ name: `${aaAdminCompanyName ? aaAdminCompanyName : companyName}` }}
      />
    );
    content.description = getDeleteContent();
    content.footer = (
      <DeleteFooter
        disableSubmit={removeInput !== intl.formatMessage(messages.removePlaceholder)}
        setShowModal={handleCancelDelete}
        handleSaveModal={() => handleConfirmDelete(true)}
        btnSpinner="deleteCompanyActionSpinner"
      />
    );
    return content;
  };

  const renderAAContent = () => {
    // Condition checks
    const isCitiCompany = removalDetails?.citi;
    const canRemove = removalDetails?.canRemove;
    const openTripCreditsExists = removalDetails?.openTripCreditsExists;
    const removalWillBeScheduled = removalDetails?.removalWillBeScheduled;

    if (!confirmDelete) {
      if (isCitiCompany) {
        content.title = <FormattedMessage id="settings.deleteModal.citiTitle" />;
        content.description = <FormattedMessage id="settings.deleteModal.citiDescription" />;
        content.footer = <CloseFooter setShowModal={setShowModal} />;
      } else if (!canRemove && isCitiCompany === false) {
        content.title = <FormattedMessage id="settings.deleteModal.travelerTitle" />;
        content.description = <FormattedMessage id="settings.deleteModal.travelerDescription" />;
        content.footer = <CloseFooter setShowModal={setShowModal} />;
      } else if (canRemove && isCitiCompany === false) {
        content.title = (
          <FormattedMessage
            id="settings.deleteAction.title"
            values={{ name: `${aaAdminCompanyName ? aaAdminCompanyName : companyName}` }}
          />
        );
        content.description = getDeleteContent();
        content.footer = (
          <DeleteFooter
            disableSubmit={removeInput !== intl.formatMessage(messages.removePlaceholder)}
            setShowModal={setShowModal}
            handleSaveModal={() => setConfirmDelete(true)}
          />
        );
      }
    }

    if (confirmDelete && canRemove) {
      content.title = (
        <FormattedMessage
          id="settings.confirmDeleteAction.title"
          values={{ name: `${aaAdminCompanyName ? aaAdminCompanyName : companyName}` }}
        />
      );
      content.description = getConfirmDeleteContent(removalWillBeScheduled, openTripCreditsExists);
      content.footer = (
        <ConfirmDeleteFooter
          disableSubmit={(removalDueToFraud && fraudCategory.trim() === "") || fraudError}
          handleSaveModal={handleConfirmDelete}
          setShowModal={handleCancelDelete}
          btnSpinner="deleteCompanyActionSpinner"
        />
      );
    }

    return content;
  };

  const getModalContent = () => (isAA ? renderAAContent() : renderNonAAContent());

  return (
    <UCModal
      className={classes.modal}
      showModal={showModal}
      setShowModal={handleCancelDelete}
      modalIcon={modalIcon}
      modalTitle={hasFetchedDetails ? modalContent.title : <ModalContentSkeleton />}
      modalDescription={modalContent.description}
      modalFooter={modalContent.footer}
      dataTestId="deleteactionmodal"
      submitCallback={deleteAction}
      onExited={() => setSelectedRow(false)}
    />
  );
}

export default DeleteCompanyMasterModal;